import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '@services/api/api.service';

const EditAdminModal = ({ isOpen, onClose, admin, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    isActive: true,
    permissionIds: []
  });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingPermissions, setFetchingPermissions] = useState(true);

  useEffect(() => {
    if (isOpen && admin) {
      setFormData({
        firstName: admin.firstName || '',
        lastName: admin.lastName || '',
        phone: admin.phone || '',
        isActive: admin.isActive !== undefined ? admin.isActive : true,
        permissionIds: admin.permissions?.map(p => p.id) || []
      });
      fetchPermissions();
    }
  }, [isOpen, admin]);

  const fetchPermissions = async () => {
    try {
      setFetchingPermissions(true);
      const response = await api.get('/admin-management/permissions');
      if (response.data.success) {
        setPermissions(response.data.data.all || []);
      }
    } catch (error) {
      console.error('Failed to fetch permissions', error);
      toast.error('Failed to load permissions');
    } finally {
      setFetchingPermissions(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permissionId)
        ? prev.permissionIds.filter(id => id !== permissionId)
        : [...prev.permissionIds, permissionId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const response = await api.put(`/admin-management/${admin.id}`, formData);
      
      if (response.data.success) {
        toast.success('Admin updated successfully');
        onSuccess();
      }
    } catch (error) {
      console.error('Failed to update admin', error);
      toast.error(error.response?.data?.message || 'Failed to update admin');
    } finally {
      setLoading(false);
    }
  };

  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {});

  if (!isOpen || !admin) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-primary-900">Edit Admin</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-primary-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Email:</strong> {admin.email}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Email cannot be changed
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-900 mb-2">
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400"
                placeholder="10-15 digits"
              />
            </div>

            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-primary-900">
                  Active Status
                </span>
              </label>
              <p className="text-xs text-primary-500 mt-1 ml-7">
                Inactive admins cannot log in
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary-900 mb-3">
                Permissions
              </label>
              {fetchingPermissions ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-primary-900">Module</th>
                        <th className="px-2 py-2 text-center text-xs font-semibold text-primary-900">Read</th>
                        <th className="px-2 py-2 text-center text-xs font-semibold text-primary-900">Write</th>
                        <th className="px-2 py-2 text-center text-xs font-semibold text-primary-900">Update</th>
                        <th className="px-2 py-2 text-center text-xs font-semibold text-primary-900">Delete</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {Object.entries(groupedPermissions).map(([resource, perms]) => {
                        const readPerm = perms.find(p => p.action === 'read');
                        const writePerm = perms.find(p => p.action === 'write');
                        const updatePerm = perms.find(p => p.action === 'update');
                        const deletePerm = perms.find(p => p.action === 'delete');
                        const managePerm = perms.find(p => p.action === 'manage');
                        const approvePerm = perms.find(p => p.action === 'approve');
                        
                        return (
                          <tr key={resource} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-primary-900 capitalize">
                              {resource}
                            </td>
                            <td className="px-2 py-3 text-center">
                              {readPerm && (
                                <input
                                  type="checkbox"
                                  checked={formData.permissionIds.includes(readPerm.id)}
                                  onChange={() => handlePermissionToggle(readPerm.id)}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                  title={readPerm.description}
                                />
                              )}
                            </td>
                            <td className="px-2 py-3 text-center">
                              {(writePerm || managePerm) && (
                                <input
                                  type="checkbox"
                                  checked={formData.permissionIds.includes((writePerm || managePerm).id)}
                                  onChange={() => handlePermissionToggle((writePerm || managePerm).id)}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                  title={(writePerm || managePerm).description}
                                />
                              )}
                            </td>
                            <td className="px-2 py-3 text-center">
                              {(updatePerm || approvePerm) && (
                                <input
                                  type="checkbox"
                                  checked={formData.permissionIds.includes((updatePerm || approvePerm).id)}
                                  onChange={() => handlePermissionToggle((updatePerm || approvePerm).id)}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                  title={(updatePerm || approvePerm).description}
                                />
                              )}
                            </td>
                            <td className="px-2 py-3 text-center">
                              {deletePerm && (
                                <input
                                  type="checkbox"
                                  checked={formData.permissionIds.includes(deletePerm.id)}
                                  onChange={() => handlePermissionToggle(deletePerm.id)}
                                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                  title={deletePerm.description}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Update Admin'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdminModal;
